import { UseQueryResult, useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/mui';

import axios from 'axios';

import { Button } from '@mui/material';
import { ItemApiResponse, url } from '../api';
import { useState } from 'react';
import { convertTimestamps, formatPhoneNumber } from '../utils';
import moment from 'moment-timezone';

const TaskForm = ({ query, show }: { query: UseQueryResult<ItemApiResponse>; show: boolean }) => {
  const {
    drx,
    rx,
    transfer,
    item,
    form_data,
    generic_call_info,
    pa_info,
    copay_card_download_info,
    insurance_info,
    pa_info_section,
    insurance_info_section,
    copay_card_info,
    provider_preference_info,
  } = query.data!;

  let initialFormData: any;
  if (item.status === 'completed') {
    initialFormData = item.result.data;
  } else {
    initialFormData = form_data;
  }
  const [formData, setFormData] = useState<any>(initialFormData);
  const itemId = item.id;
  const submit = useMutation({
    mutationKey: ['workItemSubmit', itemId],
    mutationFn: (data: any) =>
      axios.post(
        url(`/queue-item/${itemId}/complete`),
        { data },
        {
          withCredentials: true,
        },
      ),
    onSuccess: () => query.refetch(),
  });

  const snooze = useMutation({
    mutationKey: ['workItemSnooze', itemId],
    mutationFn: () =>
      axios.post(
        url(`/queue-item/${itemId}/snooze`),
        {},
        {
          withCredentials: true,
        },
      ),
    onSuccess: () => query.refetch(),
  });

  let body;

  if (item.status === 'snoozed') {
    body = <div>Item Snoozed</div>;
  }

  if (item.status === 'queued') {
    body = <div>Item Due but not yet assigned</div>;
  }

  if (item.status === 'cancelled') {
    body = <div>Item Cancelled</div>;
  }

  return (
    <FormContainer $show={show}>
      {body || (
        <>
          <InfoPanel>
            {generic_call_info && (
              <GenericPanel>
                <InfoPanelHeader>Generic Call Info</InfoPanelHeader>
                <InfoPanelSubheader>Call Type: {generic_call_info.generic_subtype}</InfoPanelSubheader>
                <InfoPanelSubheader>
                  Number to Call: {formatPhoneNumber(generic_call_info.phone_number)}
                </InfoPanelSubheader>
                <InfoPanelSubheader>Organization/Name: {generic_call_info.name}</InfoPanelSubheader>
              </GenericPanel>
            )}
            {provider_preference_info && (
              <div>
                <InfoPanelHeader>Provider Preference</InfoPanelHeader>
                <InfoPanelLine>
                  Benefits Preference: {provider_preference_info.benefits_preference.replaceAll('_', ' ')}
                </InfoPanelLine>
                <InfoPanelLine>HCPCS J-Code: {provider_preference_info.hcpcs}</InfoPanelLine>
                <InfoPanelLine>CPT Admin Code: {provider_preference_info.cpt}</InfoPanelLine>
                {provider_preference_info.care_site && (
                  <InfoPanelLine>Site of Care: {provider_preference_info.care_site.replaceAll('_', ' ')}</InfoPanelLine>
                )}
                {provider_preference_info.care_site_address && (
                  <InfoPanelLine>Care Site Address: {provider_preference_info.care_site_address}</InfoPanelLine>
                )}
                {provider_preference_info.rendering_provider_npi && (
                  <InfoPanelLine>Rendering NPI: {provider_preference_info.rendering_provider_npi}</InfoPanelLine>
                )}
                {provider_preference_info.tin && <InfoPanelLine>TIN: {provider_preference_info.tin}</InfoPanelLine>}
              </div>
            )}
            {copay_card_download_info && (
              <CopayCardDownloadPanel>
                <InfoPanelHeader>Copay Card Info</InfoPanelHeader>
                <InfoPanelLine>
                  {copay_card_download_info.copay_card_url === null ? (
                    'Link: missing'
                  ) : (
                    <a href={`${copay_card_download_info.copay_card_url}`} target="_blank" rel="noopener noreferrer">
                      Copay card link
                    </a>
                  )}
                </InfoPanelLine>
                <InfoPanelLine>Gender: {copay_card_download_info.gender}</InfoPanelLine>
                <InfoPanelLine>Address line 1: {copay_card_download_info.address_line_1}</InfoPanelLine>
                <InfoPanelLine>Address line 2: {copay_card_download_info.address_line_2}</InfoPanelLine>
                <InfoPanelLine>City: {copay_card_download_info.address_city}</InfoPanelLine>
                <InfoPanelLine>State: {copay_card_download_info.address_state}</InfoPanelLine>
                <InfoPanelLine>ZIP: {copay_card_download_info.address_zip_code}</InfoPanelLine>
                <InfoPanelLine>Email: {copay_card_download_info.email_address}</InfoPanelLine>
              </CopayCardDownloadPanel>
            )}
            {transfer && (
              <div>
                {transfer.channel === 'ehr_represcribe' ? (
                  <InfoPanelHeader>Electronically Prescribed from Provider</InfoPanelHeader>
                ) : (
                  <InfoPanelHeader>Transferred from Tandem Pharmacy</InfoPanelHeader>
                )}
                <InfoPanelLine>
                  Sent: {moment(transfer.created_at).tz('America/New_York').format('MMM DD, YYYY h:mm')}
                </InfoPanelLine>
                {transfer.pharmacy_json.pharmacy_type === 'retail' && (
                  <InfoPanelLine>
                    Tandem Provider Relationship: If they ask what Tandem's relationship is with the provider, tell them
                    "Tandem partners with the provider's office to complete prior authorizations for prescription drugs
                    and help patients with medication access."
                  </InfoPanelLine>
                )}
                {transfer.channel === 'ehr_represcribe' ? (
                  <InfoPanelLine>
                    Instructions: Call on behalf of the PROVIDER to confirm the insurance status and price. This is NOT
                    a transfer.
                  </InfoPanelLine>
                ) : (
                  <InfoPanelLine>
                    <a href={url(`/queue-item/${itemId}/pdf`)} target="_blank">
                      Fax Transfer from Tandem: Show PDF
                    </a>
                  </InfoPanelLine>
                )}
              </div>
            )}
            {drx && rx && (
              <div>
                <InfoPanelHeader>Drug Info</InfoPanelHeader>
                <InfoPanelSubheader>{drx.drug_description}</InfoPanelSubheader>
                <InfoPanelLine>NDC: {rx.drug_ndc}</InfoPanelLine>
                <InfoPanelLine>Quantity: {rx.quantity}</InfoPanelLine>
                <InfoPanelLine>Days Supply: {rx.days_supply}</InfoPanelLine>
                <InfoPanelLine>Sig: {rx.sig}</InfoPanelLine>
                <InfoPanelLine>Provider Notes: {drx.provider_instructions}</InfoPanelLine>
                <InfoPanelLine>Refills: {rx.refills}</InfoPanelLine>
                <InfoPanelLine>Diagnosis Codes: {rx.diagnosis_codes}</InfoPanelLine>
                {rx.pa_status && <InfoPanelLine>PA Information: {rx.pa_status}</InfoPanelLine>}
              </div>
            )}
            {copay_card_info && (
              <div>
                <InfoPanelHeader>Copay Card Info</InfoPanelHeader>
                <InfoPanelSubheader>{copay_card_info.drug}</InfoPanelSubheader>
                <InfoPanelLine>Cardholder ID: {copay_card_info.cardholder_id}</InfoPanelLine>
                <InfoPanelLine>BIN: {copay_card_info.bin}</InfoPanelLine>
                <InfoPanelLine>PCN: {copay_card_info.pcn}</InfoPanelLine>
                <InfoPanelLine>Group: {copay_card_info.group}</InfoPanelLine>
                <InfoPanelLine>Retrieved: {copay_card_info.retrieved_at}</InfoPanelLine>
                <InfoPanelLine>Marked Inactive: {String(copay_card_info.marked_inactive)}</InfoPanelLine>
              </div>
            )}
            {drx && (
              <div>
                <InfoPanelHeader>Provider</InfoPanelHeader>
                <InfoPanelSubheader>
                  {drx.prescriber_first_name} {drx.prescriber_last_name}
                </InfoPanelSubheader>
                <InfoPanelLine>Practice Name: {drx.practice_name || 'Not Available'}</InfoPanelLine>
                <InfoPanelLine>Office TIN: {drx.office_tin || 'Not Available'}</InfoPanelLine>
                <InfoPanelLine>NPI: {drx.prescriber_npi}</InfoPanelLine>
                <InfoPanelLine>Phone: {formatPhoneNumber(drx.prescriber_primary_phone)}</InfoPanelLine>
                <InfoPanelLine>Fax: {drx.prescriber_fax && formatPhoneNumber(drx.prescriber_fax)}</InfoPanelLine>
                <InfoPanelLine>
                  Address: {drx.prescriber_address_line_1} {drx.prescriber_address_line_2} {drx.prescriber_address_city}
                  {', '}
                  {drx.prescriber_address_state} {drx.prescriber_address_postal_code}
                </InfoPanelLine>
              </div>
            )}
            {transfer && transfer.coupon_json && (
              <div>
                <InfoPanelHeader>Coupon Card</InfoPanelHeader>
                <InfoPanelSubheader>{transfer.coupon_json.name}</InfoPanelSubheader>
                <InfoPanelLine>ID: {transfer.coupon_json.id}</InfoPanelLine>
                <InfoPanelLine>BIN: {transfer.coupon_json.bin}</InfoPanelLine>
                <InfoPanelLine>Group: {transfer.coupon_json.group_id}</InfoPanelLine>
                <InfoPanelLine>PCN: {transfer.coupon_json.pcn}</InfoPanelLine>
              </div>
            )}
            {pa_info_section && (
              <div>
                <InfoPanelHeader>PA / Appeal Info</InfoPanelHeader>
                <InfoPanelLine>PA / Appeal Status: {pa_info_section.pa_status}</InfoPanelLine>
                <InfoPanelLine>Additional Instruction: {pa_info_section.additional_instruction}</InfoPanelLine>
                <InfoPanelLine>Submitted On: {pa_info_section.submitted_on}</InfoPanelLine>
                <InfoPanelLine>Approved Until: {pa_info_section.approved_until}</InfoPanelLine>
                <InfoPanelLine>PA Documents:</InfoPanelLine>
                {pa_info_section.pa_documents ? (
                  pa_info_section.pa_documents.map((id, idx) => (
                    <InfoPanelLine key={id}>
                      -{' '}
                      <a href={url(`/view-document-id/${id}`)} target="_blank">
                        Document {idx + 1}
                      </a>
                    </InfoPanelLine>
                  ))
                ) : (
                  <InfoPanelLine>- No PA documents available to view.</InfoPanelLine>
                )}

                <InfoPanelLine>
                  Submitted Insurance ID: {pa_info_section?.insurance_info?.insurance_cardholder_id}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance RxBIN: {pa_info_section?.insurance_info?.insurance_bin}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance RxPCN: {pa_info_section?.insurance_info?.insurance_pcn}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance RxGroup ID: {pa_info_section?.insurance_info?.insurance_group_id}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance Name: {pa_info_section?.insurance_info?.insurance_name}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance Patient First Name:{' '}
                  {pa_info_section?.insurance_info?.insurance_patient_first_name}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance Patient Last Name: {pa_info_section?.insurance_info?.insurance_patient_last_name}
                </InfoPanelLine>
                <InfoPanelLine>
                  Submitted Insurance Patient Zipcode: {pa_info_section?.insurance_info?.insurance_patient_zipcode}
                </InfoPanelLine>
                <InfoPanelLine>Submitted Insurance Document IDs:</InfoPanelLine>
                {pa_info_section?.insurance_info?.insurance_document_ids ? (
                  pa_info_section?.insurance_info?.insurance_document_ids.map((id, idx) => (
                    <InfoPanelLine key={id}>
                      -{' '}
                      <a href={url(`/view-document-id/${id}`)} target="_blank">
                        Document {idx + 1}
                      </a>
                    </InfoPanelLine>
                  ))
                ) : (
                  <InfoPanelLine>- No submitted insurance documents available to view.</InfoPanelLine>
                )}
              </div>
            )}
            {!pa_info_section && pa_info && (
              <div>
                <InfoPanelHeader>PA Info</InfoPanelHeader>
                <InfoPanelLine>External Plan ID: {pa_info.external_plan_id}</InfoPanelLine>
                <InfoPanelLine>Submission Mode: {pa_info.submission_mode}</InfoPanelLine>
                <InfoPanelLine>First Submitted On Date: {pa_info.first_submitted_on_date}</InfoPanelLine>
                <InfoPanelLine>Insurance Card Holder ID: {pa_info.insurance_cardholder_id}</InfoPanelLine>
                <InfoPanelLine>Submitted Fax Numbers: {pa_info.submitted_fax_numbers}</InfoPanelLine>
                <InfoPanelLine>Insurance Cards:</InfoPanelLine>
                {pa_info.insurance_document_ids ? (
                  pa_info.insurance_document_ids.map((id, idx) => (
                    <InfoPanelLine key={id}>
                      -{' '}
                      <a href={url(`/view-document-id/${id}`)} target="_blank">
                        Card {idx + 1}
                      </a>
                    </InfoPanelLine>
                  ))
                ) : (
                  <InfoPanelLine>- No insurance cards available to view.</InfoPanelLine>
                )}
              </div>
            )}
            {insurance_info_section && (
              <div>
                <InfoPanelHeader>Insurance Info</InfoPanelHeader>
                <InfoPanelLine>Cardholder ID: {insurance_info_section.insurance_cardholder_id}</InfoPanelLine>
                <InfoPanelLine>RxBIN: {insurance_info_section.insurance_bin}</InfoPanelLine>
                <InfoPanelLine>RxPCN: {insurance_info_section.insurance_pcn}</InfoPanelLine>
                <InfoPanelLine>RxGroup ID: {insurance_info_section.insurance_group_id}</InfoPanelLine>
                <InfoPanelLine>Insurance Name: {insurance_info_section.insurance_name}</InfoPanelLine>
                <InfoPanelLine>Patient First Name: {insurance_info_section.insurance_patient_first_name}</InfoPanelLine>
                <InfoPanelLine>Patient Last Name: {insurance_info_section.insurance_patient_last_name}</InfoPanelLine>
                <InfoPanelLine>Patient Zipcode: {insurance_info_section.insurance_patient_zipcode}</InfoPanelLine>
                <InfoPanelLine>Insurance Document IDs:</InfoPanelLine>
                {insurance_info_section.insurance_document_ids ? (
                  insurance_info_section.insurance_document_ids.map((id, idx) => (
                    <InfoPanelLine key={id}>
                      -{' '}
                      <a href={url(`/view-document-id/${id}`)} target="_blank">
                        Document {idx + 1}
                      </a>
                    </InfoPanelLine>
                  ))
                ) : (
                  <InfoPanelLine>- No insurance documents available to view.</InfoPanelLine>
                )}
              </div>
            )}
            {!insurance_info_section && insurance_info && (
              <div>
                <InfoPanelHeader>Insurance Info</InfoPanelHeader>
                <InfoPanelLine>Cardholder ID: {insurance_info.cardholder_id}</InfoPanelLine>
                <InfoPanelLine>RxBIN: {insurance_info.bin}</InfoPanelLine>
                <InfoPanelLine>RxPCN: {insurance_info.pcn}</InfoPanelLine>
                <InfoPanelLine>RxGroup ID: {insurance_info.group_id}</InfoPanelLine>
                <InfoPanelLine>Insurance Name: {insurance_info.name}</InfoPanelLine>
                <InfoPanelLine>Patient Zipcode: {insurance_info.patient_zipcode}</InfoPanelLine>
                <InfoPanelLine>Patient First Name: {insurance_info.patient_first_name}</InfoPanelLine>
                <InfoPanelLine>Patient Last Name: {insurance_info.patient_last_name}</InfoPanelLine>
                <InfoPanelLine>Insurance Cards:</InfoPanelLine>
                {insurance_info.insurance_document_ids ? (
                  insurance_info.insurance_document_ids.map((id, idx) => (
                    <InfoPanelLine key={id}>
                      -{' '}
                      <a href={url(`/view-document-id/${id}`)} target="_blank">
                        Card {idx + 1}
                      </a>
                    </InfoPanelLine>
                  ))
                ) : (
                  <InfoPanelLine>- No insurance cards available to view.</InfoPanelLine>
                )}
              </div>
            )}
          </InfoPanel>
          <FormPanel>
            <h2>
              {item.status === 'completed'
                ? `Intake Form - Completed ${moment(item.status_updated_at)
                    .tz('America/New_York')
                    .format('MMM DD, YYYY h:mm A')}`
                : 'Intake Form'}
            </h2>
            <Form
              disabled={item.status === 'completed'}
              schema={item.form_schema}
              uiSchema={item.ui_schema}
              validator={validator}
              formData={formData}
              omitExtraData={true}
              liveOmit={true}
              onSubmit={(event) => submit.mutate(convertTimestamps(event.formData))}
              onChange={(event) => {
                // eslint-disable-next-line no-console
                import.meta.env.DEV && console.log(event.formData);
                setFormData(event.formData);
              }}
            >
              <Button type="submit" disabled={item.status === 'completed'}>
                Submit
              </Button>
              <Button type="button" disabled={item.status === 'completed'} onClick={() => snooze.mutate()}>
                Snooze
              </Button>
            </Form>
          </FormPanel>
        </>
      )}
    </FormContainer>
  );
};

const FormContainer = styled.div<{ $show: boolean }>`
  display: flex;
  flex-direction: row;
  display: ${(props) => (props.$show ? 'flex' : 'none')};
  padding: 24px;
  background: var(--white);
  border-top: 1px solid var(--grayscale-100);
  gap: 24px;
  justify-content: space-between;
`;

// TODO: use Typography component
const InfoPanel = styled.div`
  flex: 0 0 480px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Space Grotesk', system-ui, Helvetica, Arial, sans-serif;
`;

const FormPanel = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: 12px;
  max-width: 650px;
`;

const GenericPanel = styled.div`
  border: 1px solid var(--grayscale-900);
`;
const CopayCardDownloadPanel = styled.div`
  border: 1px solid var(--grayscale-900);
  padding: 5px;
`;

// TODO: use Typography component
const InfoPanelHeader = styled.div`
  color: var(--grayscale-900);
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  text-align: left;
  text-transform: capitalize;
`;

// TODO: use Typography component
const InfoPanelSubheader = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

// TODO: use Typography component
const InfoPanelLine = styled.div`
  font-size: 14px;
  font-weight: 300;
`;

export default TaskForm;
